import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TransferSizeMatrix, { codeOf, myanmarReason } from './TransferSizeMatrix';
import './WarehouseAllocationPanel.css';

export default function WarehouseTransferA4Preview({ plan, onClose }) {
  const preview = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const from = plan.fromLocationCode ?? plan.from_location_code;
  const to = plan.toLocationCode ?? plan.to_location_code;
  const date = plan.generatedAt ?? plan.updated_at ?? plan.createdAt ?? plan.created_at;
  const ai = plan.aiSummary ?? plan.ai_summary;
  const meta = <div className="warehouseAllocation__printMeta">
    <span><b>From</b> {from}</span><span><b>To</b> {to}</span><span><b>Date</b> {date ? new Date(date).toLocaleString() : '-'}</span>
    <span><b>Transfer ID</b> {plan.id}</span><span><b>Version</b> {plan.version}</span><span><b>Status</b> {plan.status}</span>
  </div>;
  useEffect(() => {
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; document.body.classList.add('warehouseA4Open'); preview.current?.focus();
    return () => { document.body.style.overflow = previousOverflow; document.body.classList.remove('warehouseA4Open'); if (trigger?.isConnected) trigger.focus(); };
  }, []);
  async function print() {
    setPrinting(true); setError('');
    try { await document.fonts?.ready; window.print(); }
    catch (err) { setError('Print မဖွင့်နိုင်ပါ။ Browser ရဲ့ Print menu မှတစ်ဆင့် A4 ထုတ်ပါ။'); }
    finally { setPrinting(false); }
  }
  const note = plan.status === 'SUGGESTED'
    ? 'အကြံပြုစာရင်းသာဖြစ်ပါသည်။ Save မလုပ်ရသေးသဖြင့် ပစ္စည်းမပို့ပါနှင့်။'
    : plan.status === 'CANCELLED'
      ? 'ဤ transfer ကို ပယ်ဖျက်ထားပါသည်။ ဤစာရွက်ဖြင့် ပစ္စည်းမပို့ပါနှင့်။'
      : plan.status === 'READY'
        ? 'သိမ်းထားသောစာရင်းအတိုင်း code / colour / size / qty တူညီအောင် တကယ့်ပစ္စည်းများကို scan ဖတ်ပြီးမှ ပို့ပါ။'
        : 'ပို့ပြီးသော transfer မှတ်တမ်းဖြစ်ပါသည်။ ဤစာရွက်ဖြင့် ထပ်မပို့ပါနှင့်။';
  return createPortal(<div className="warehouseAllocation__previewBackdrop" onMouseDown={event => { if (event.target === event.currentTarget) close.current(); }}>
    <div ref={preview} tabIndex={-1} className="warehouseAllocation__preview" role="dialog" aria-modal="true" aria-label="A4 transfer preview" onKeyDown={event => {
      if (event.key === 'Escape') close.current();
      if (event.key === 'Tab') {
        const buttons = [...preview.current.querySelectorAll('button')].filter(button => !button.disabled); const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === preview.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }}>
      <div className="warehouseAllocation__previewToolbar"><strong>A4 Preview · {from} → {to} · v{plan.version}</strong><div><button type="button" disabled={printing} onClick={print}>{printing ? 'Preparing fonts…' : 'Print A4'}</button><button type="button" onClick={() => close.current()}>Close</button></div></div>
      {error && <p role="alert" className="warehouseAllocation__error">{error}</p>}
      <div className="warehouseAllocation__print">
        <article className="warehouseAllocation__sheet"><header><div><small>{plan.brand}</small><h1>TRANSFER PLAN</h1></div><strong>{plan.status === 'SUGGESTED' ? 'DRAFT · NOT FOR DISPATCH' : plan.status}</strong></header>
          {meta}<TransferSizeMatrix plan={plan} />
          <p className="warehouseAllocation__printNote" lang="my">{note}</p>
          <div className="warehouseAllocation__sign"><span>Prepared by __________________</span><span>Checked by __________________</span><span>Received by __________________</span><span>Account __________________</span></div>
        </article>
        <article className="warehouseAllocation__sheet warehouseAllocation__sheet--summary"><header><div><small>{plan.brand}</small><h1>TRANSFER SUMMARY</h1></div><strong>{plan.status}</strong></header>
          {meta}<h2 lang="my">ပို့ရန် အကြံပြုရသည့် အကြောင်းရင်း</h2>
          {/[\u1000-\u109f]/.test(ai || '') && <p className="warehouseAllocation__printReason" lang="my">{ai}</p>}
          <table lang="my"><thead><tr><th>Article Number</th><th>Colour</th><th>Size</th><th>Qty</th><th>အကြံပြုရှင်းလင်းချက်</th></tr></thead><tbody>{plan.items.map(item => <tr key={item.id}><td>{codeOf(item)}</td><td>{item.color}</td><td>{item.size}</td><td>{item.qty}</td><td>{myanmarReason(item,plan)}</td></tr>)}</tbody></table>
          <p className="warehouseAllocation__printNote" lang="my">ဤစာရင်းသည် WS staff စစ်ဆေးရန်ဖြစ်ပါသည်။ A4 ထုတ်ခြင်းက inventory စာရင်းကို မပြောင်းလဲပါ။</p>
        </article>
      </div>
    </div>
  </div>, document.body);
}
